import { z } from 'zod'

/**
 * =============================================================================
 * VALIDACIÓN DE VARIABLES DE ENTORNO
 * =============================================================================
 *
 * Todo lo que empieza con VITE_ se incrusta en el JavaScript que descarga
 * el navegador y, por lo tanto, es público. Nunca colocar secretos aquí.
 * =============================================================================
 */

const envSchema = z.object({
  /**
   * URL base del backend.
   *
   * Por ahora se permite tanto http:// como https:// para soportar
   * despliegues de desarrollo por IP.
   *
   * Cuando exista un entorno productivo formal, la política de HTTPS debe
   * depender del entorno de despliegue y no de import.meta.env.PROD.
   */
  VITE_API_URL: z.url({
    message: 'VITE_API_URL debe ser una URL válida (ej: http://72.60.69.232:3000)',
  }),

  /**
   * Nombre visible de la aplicación.
   */
  VITE_APP_NAME: z.string().min(1).default('Alto Palermo'),

  /**
   * Activa herramientas de desarrollo.
   */
  VITE_ENABLE_DEVTOOLS: z
    .string()
    .optional()
    .transform((value) => value === 'true'),
})

export type Env = z.infer<typeof envSchema>

function loadEnv(): Env {
  const result = envSchema.safeParse(import.meta.env)

  if (!result.success) {
    const details = result.error.issues
      .map((issue) => `  • ${issue.path.join('.')}: ${issue.message}`)
      .join('\n')

    throw new Error(
      `\n❌ Variables de entorno inválidas:\n\n${details}\n\n` +
        'Revisa la configuración de variables de entorno del deployment.\n',
    )
  }

  return result.data
}

/**
 * Configuración validada de la aplicación.
 *
 * Usar:
 * import { env } from '@/shared/config/env'
 *
 * en lugar de acceder directamente a import.meta.env por toda la aplicación.
 */
export const env = loadEnv()

export const isDevelopment = import.meta.env.DEV
export const isProduction = import.meta.env.PROD
